// Tenantra Inc. - Team notes on a tenant, property or applicant: @mention teammates, editable for 5 minutes, then locked for audit
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { Textarea } from '@/components/ui/textarea';
import { Section } from './DetailPanel';

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ago = (iso) => {
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

function Body({ text, names }) {
  if (!names.length) return text;
  const tags = names.map(n => `@${n}`);
  return text.split(new RegExp(`(${tags.map(esc).join('|')})`, 'g')).map((part, i) => (tags.includes(part) ? <span key={i} className="text-teal-700 font-medium">{part}</span> : part));
}

function MentionList({ q, matches, active, onPick, hasMates }) {
  if (!q) return null;
  return (
    <ul className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg ring-1 ring-slate-900/5 py-1 text-sm" data-testid="note-mention-list">
      {!hasMates ? <li className="px-3 py-2 text-xs text-slate-500" data-testid="note-mention-empty">Invite teammates from the Team page to mention them.</li>
        : matches.length === 0 ? <li className="px-3 py-2 text-xs text-slate-500">No teammate matches “{q.query}”</li>
        : matches.map((m, i) => (
          <li key={m.user_id}>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(m); }} className={`w-full text-left px-3 py-1.5 flex items-center justify-between gap-2 transition-colors ${i === active ? 'bg-teal-50 text-teal-800' : 'text-slate-700 hover:bg-slate-50'}`} data-testid={`note-mention-option-${m.user_id}`}>
              <span className="truncate">{m.name}</span><span className="text-[11px] text-slate-400 truncate">{m.owner ? 'Owner' : m.email}</span>
            </button>
          </li>
        ))}
    </ul>
  );
}

function Composer({ team, me, initial = '', initialMentions = [], onSubmit, onCancel, label, testId, hint }) {
  const [text, setText] = useState(initial);
  const [mentions, setMentions] = useState(initialMentions);
  const [q, setQ] = useState(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const mates = useMemo(() => team.filter(m => m.user_id !== me), [team, me]);
  const matches = q ? mates.filter(m => m.name.toLowerCase().includes(q.query.toLowerCase())).slice(0, 6) : [];
  const onChange = (e) => {
    const v = e.target.value; const caret = e.target.selectionStart;
    const m = v.slice(0, caret).match(/(^|\s)@([^\s@]{0,20}(?: [^\s@]{0,20})?)$/);
    setText(v); setQ(m ? { query: m[2], start: caret - m[2].length - 1, end: caret } : null); setActive(0);
  };
  const pick = (m) => {
    const tag = `@${m.name} `;
    const next = text.slice(0, q.start) + tag + text.slice(q.end);
    setText(next); setMentions(ids => [...new Set([...ids, m.user_id])]); setQ(null);
    requestAnimationFrame(() => { const el = ref.current; if (el) { el.focus(); el.setSelectionRange(q.start + tag.length, q.start + tag.length); } });
  };
  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(text.trim(), mentions.filter(id => { const m = mates.find(x => x.user_id === id); return m && text.includes(`@${m.name}`); }));
      if (!initial) { setText(''); setMentions([]); }
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  const onKeyDown = (e) => {
    if (q && matches.length && ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'].includes(e.key)) {
      e.preventDefault();
      if (e.key === 'ArrowDown') setActive(a => (a + 1) % matches.length);
      else if (e.key === 'ArrowUp') setActive(a => (a - 1 + matches.length) % matches.length);
      else pick(matches[active]);
    } else if (e.key === 'Escape' && q) { e.stopPropagation(); setQ(null); }
    else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="relative">
        <Textarea ref={ref} value={text} onChange={onChange} onKeyDown={onKeyDown} onBlur={() => setTimeout(() => setQ(null), 120)} maxLength={4000} rows={initial ? 3 : 2}
          aria-label="Note" className="text-sm leading-relaxed resize-none" data-testid={`${testId}-input`} />
        <MentionList q={q} matches={matches} active={active} onPick={pick} hasMates={mates.length > 0} />
      </div>
      <div className="flex items-center justify-end gap-2">
        {hint && <p className="mr-auto text-[11px] text-slate-400 leading-snug">{hint}</p>}
        {onCancel && <button type="button" onClick={onCancel} className="h-8 px-3 rounded-md text-xs text-slate-600 hover:bg-slate-100 transition-colors" data-testid={`${testId}-cancel`}>Cancel</button>}
        <button type="button" onClick={submit} disabled={busy || !text.trim()} className="h-8 px-3 rounded-md bg-teal-600 hover:bg-teal-700 transition-colors text-white text-xs font-medium disabled:opacity-40 inline-flex items-center gap-1.5" data-testid={`${testId}-submit`}>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{label}
        </button>
      </div>
    </div>
  );
}

function NoteItem({ n, team, me, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [, tick] = useState(0);
  const deadline = useMemo(() => Date.now() + n.seconds_left * 1000, [n]);
  useEffect(() => { if (n.seconds_left <= 0) return undefined; const t = setInterval(() => tick(x => x + 1), 5000); return () => clearInterval(t); }, [n]);
  const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const names = team.filter(m => n.mentions.includes(m.user_id)).map(m => m.name);
  const save = async (body, mentions) => { await api.patch(`/landlord/notes/${n.id}`, { body, mentions }); toast.success('Note updated'); setEditing(false); onChanged(); };
  const remove = async () => {
    try { await api.delete(`/landlord/notes/${n.id}`); toast.success('Note deleted'); onChanged(); } catch (e) { toast.error(apiHelpers.handleError(e)); setConfirm(false); onChanged(); }
  };
  return (
    <li className="py-3" data-testid={`note-${n.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs text-slate-500 truncate"><span className="text-slate-800 font-medium">{n.author_id === me ? 'You' : n.author_name}</span> · {ago(n.created_at)}{n.edited && ' · edited'}</p>
        {left > 0 && !editing ? (
          <span className="flex items-center gap-3 text-[11px] flex-shrink-0">
            {confirm ? <>
              <span className="text-slate-500">Delete this note?</span>
              <button onClick={remove} className="font-medium text-red-600 hover:text-red-700" data-testid={`note-delete-confirm-${n.id}`}>Delete</button>
              <button onClick={() => setConfirm(false)} className="text-slate-500 hover:text-slate-700" data-testid={`note-delete-cancel-${n.id}`}>Keep</button>
            </> : <>
              <span className="text-slate-400" data-testid={`note-window-${n.id}`}>{Math.ceil(left / 60)} min left to change</span>
              <button onClick={() => setEditing(true)} className="font-medium text-teal-700 hover:text-teal-800" data-testid={`note-edit-${n.id}`}>Edit</button>
              <button onClick={() => setConfirm(true)} className="text-slate-500 hover:text-red-600" data-testid={`note-delete-${n.id}`}>Delete</button>
            </>}
          </span>
        ) : !editing && n.author_id === me && <Lock className="w-3 h-3 text-slate-300 flex-shrink-0" aria-label="Locked for your records" data-testid={`note-locked-${n.id}`} />}
      </div>
      {editing ? <div className="mt-2"><Composer team={team} me={me} initial={n.body} initialMentions={n.mentions} onSubmit={save} onCancel={() => setEditing(false)} label="Save" testId={`note-editor-${n.id}`} /></div>
        : <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed" data-testid={`note-body-${n.id}`}><Body text={n.body} names={names} /></p>}
    </li>
  );
}

export function EntityNotes({ type, id }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/landlord/notes', { params: { entity_type: type, entity_id: id } }).then(r => setData(r.data)).catch(() => setData(false)), [type, id]);
  useEffect(() => { setData(null); load(); }, [load]);
  if (data === false) return null;
  const add = async (body, mentions) => {
    await api.post('/landlord/notes', { entity_type: type, entity_id: id, body, mentions });
    toast.success(mentions.length ? `Note added. ${mentions.length === 1 ? 'Your teammate has' : 'Your teammates have'} been notified.` : 'Note added');
    load();
  };
  return (
    <Section title="Notes" aside="Visible to your team">
      {!data ? <div className="h-16 bg-slate-100 rounded animate-pulse" /> : <div data-testid={`notes-${type}`}>
        <Composer team={data.team} me={data.me} onSubmit={add} label="Add note" testId="note-composer" hint="Type @ to mention a teammate. Notes lock 5 minutes after posting." />
        {data.notes.length === 0 ? <p className="text-sm text-slate-500 pt-3" data-testid="notes-empty">No notes yet.</p>
          : <ul className="divide-y divide-slate-100 mt-2" data-testid="notes-list">{data.notes.map(n => <NoteItem key={`${n.id}-${n.edited_at}`} n={n} team={data.team} me={data.me} onChanged={load} />)}</ul>}
      </div>}
    </Section>
  );
}
